import { mutate } from "swr";
import { useFormik } from "formik";
import { enqueueSnackbar } from "notistack";
import React, { useState, useEffect } from "react";

import { ITemp } from "../users-table";
import usePutApi from "../../../../hooks/use-put-api";
import parseNumber from "../../../../utils/parse-number";
import endpoints, { IMember } from "../../../../api/endpoints";
import WithDrawSchema, { IWithDrawSchema } from "./schema/withdraw.schema";
import { showErrorToast, showSuccessToast } from "src/utils/toast";
import { useSyncGlobalPageLoader } from "../../../../contexts/global-page-loader-context";

interface Props {
  openModal: boolean;
  toggleModal: () => void;
  rowData: ITemp;
  parentData: IMember | undefined;
  parentUserId: string;
}

const WithdrawModal: React.FC<Props> = ({ openModal, toggleModal, rowData, parentData, parentUserId }) => {
  const [downBalance, setDownBalance] = useState(0);

  const [remainingUpBalance, setRemainingUpBalance] = useState(0);

  const [remainingDownBalance, setRemainingDownBalance] = useState(0);

  const { values, errors, handleChange, handleSubmit, handleBlur, resetForm, setFieldValue } = useFormik({
    initialValues: {
      remark: "",
      amount: "",
      transaction_password: "",
      member_operation_type: "withdraw",
    },
    onSubmit: (formValues) => {
      handleWithdrawSubmit({ ...formValues, amount: +values.amount });
    },
    validationSchema: WithDrawSchema,
  });

  const { putData, loading: putLoading } = usePutApi(endpoints.member.operation(rowData.id), [
    endpoints.member.getMember(parentUserId),
    endpoints.member.fetchDownLineList(parentUserId, 25, 0, "active"),
    endpoints.member.fetchDownLineList(parentUserId, 25, 0, "inactive"),
    endpoints.member.fetchDownLineList(parentUserId, 25, 0, "both"),
  ]);

  useSyncGlobalPageLoader(putLoading);

  const handleWithdrawSubmit = async (submitData: IWithDrawSchema) => {
    try {
      await putData({
        ...submitData,
        admin_id: parentUserId,
        amount: parseNumber({ type: "float", value: values.amount }),
      });
      showSuccessToast("Withdraw Successful");
      toggleModal();
      resetForm();
    } catch (error: any) {
      const errorMessage = error?.data?.status_message;
      showErrorToast(errorMessage || "Someting Went Wrong");
      toggleModal();
      resetForm();
    }
  };

  useEffect(() => {
    if (rowData && rowData?.available_balance) {
      setDownBalance(rowData?.available_balance);
    }
  }, [rowData]);

  useEffect(() => {
    setRemainingUpBalance((parentData?.balance ?? 0) + +values.amount);
    setRemainingDownBalance(downBalance - +values.amount);
  }, [values.amount, downBalance, parentData?.balance]);

  useEffect(() => {
    mutate(endpoints.member.getMember(parentUserId));
  }, [parentUserId]);

  if (!openModal) return null;

  return (
    <>
    {/* // style={{ position: "absolute", zIndex: 1040 }} */}
      <div
        role="dialog"
        className="modal fade show"
        aria-modal="true"
        style={{ display: "block" }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            toggleModal();
          }
        }}
      >
        <div className="modal-dialog modal-md modal-dialog-scrollable">
          <span />
          <div className="modal-content" style={{ marginTop: "60px" }}>
            <header className="modal-header">
              <h4 className="modal-title">Withdraw</h4>
              <button type="button" data-dismiss="modal" className="close text-white" onClick={toggleModal}>
                &times;
              </button>
            </header>
            <div className="modal-body">
              <form method="post" onSubmit={handleSubmit}>
                <div className="form-group row">
                  <label className="col-form-label col-4" htmlFor="userWithdrawloginusramount">{parentData?.user_name}</label>
                  <div className="col-8">
                    <div className="row">
                      <div className="col-6">
                        <input
                          id="userWithdrawloginusramount"
                          type="text"
                          readOnly
                          value={parentData?.balance ?? 0}
                          name="userWithdrawloginusramount"
                          className="form-control txt-right"
                        />
                      </div>
                      <div className="col-6">
                        <input
                          type="text"
                          readOnly
                          value={+values.amount > 0 ? remainingUpBalance : (parentData?.balance ?? 0)}
                          name="userWithdrawloginusrNamount"
                          className="form-control txt-right"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-form-label col-4" htmlFor="userWithdrawusrnameamount">{rowData.username}</label>
                  <div className="col-8">
                    <div className="row">
                      <div className="col-6">
                        <input
                          id="userWithdrawusrnameamount"
                          type="text"
                          readOnly
                          value={rowData?.available_balance ?? 0}
                          name="userWithdrawusrnameamount"
                          className="form-control txt-right"
                        />
                      </div>
                      <div className="col-6">
                        <input
                          type="text"
                          readOnly
                          value={+values.amount > 0 ? remainingDownBalance : "0"}
                          name="userWithdrawusrnameNamount"
                          className="form-control txt-right"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-form-label col-4" htmlFor="userWithdrawamount">Amount</label>
                  <div className="col-8 form-group-feedback form-group-feedback-right">
                    <input
                      id="userWithdrawamount"
                      type="number"
                      placeholder="Amount"
                      name="amount"
                      className={`form-control txt-right ${errors.amount ? "is-invalid" : ""}`}
                      value={values.amount}
                      onClick={() => {
                        if (+values.amount === 0) {
                          setFieldValue("amount", "");
                        }
                      }}
                      onChange={handleChange}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (values.amount === "") {
                          setFieldValue("amount", "");
                        }
                      }}
                    />
                    {/* {errors.amount && <span className="text-danger small">{errors.amount}</span>} */}
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-form-label col-4" htmlFor="userWithdrawremark">Remark</label>
                  <div className="col-8 form-group-feedback form-group-feedback-right">
                    <textarea
                      id="userWithdrawremark"
                      placeholder="Remark"
                      name="remark"
                      className={`form-control ${errors.remark ? "is-invalid" : ""}`}
                      value={values.remark}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {/* {errors.remark && <span className="text-danger small">{errors.remark}</span>} */}
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-form-label col-4" htmlFor="userWithdrawpassword">Transaction Password</label>
                  <div className="col-8 form-group-feedback form-group-feedback-right">
                    <input
                      id="userWithdrawpassword"
                      name="transaction_password"
                      type="password"
                      className={`form-control ${errors.transaction_password?"is-invalid":""}`}
                      value={values.transaction_password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {/* {errors.transaction_password && (
                      <span className="text-danger small">{errors.transaction_password}</span>
                    )} */}
                  </div>
                </div>

                <div className="form-group row">
                  <div className="col-12 text-right">
                    <button type="button" className="btn btn-back" onClick={toggleModal}>
                      <i className="fas fa-undo mr-1" />
                      Back
                    </button>{" "}
                    <button type="submit" className="btn btn-primary">
                      Submit
                      <i className="fas fa-sign-in-alt ml-1" />
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <span />
        </div>
      </div>
      <div className="modal-backdrop" role="button" tabIndex={0} aria-label="Close withdraw modal" onClick={toggleModal} />
    </>
  );
};

export default WithdrawModal;
